import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useRouteMatch } from 'react-router'
import ModalContext from 'contexts/ModalContext'
import MasterModalContext from 'contexts/MasterModalContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import WrappedDiscoverModal from 'components/Pages/Wrapped/WrappedDiscoverModal'
import { extractCookie, setCookie } from 'lib/web/cookieUtils'
import { fetchWrapped } from 'actions/WrappedActions'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'

const ResubModalExcludedRoutes = [
  '/:regionCode/account',
  '/:regionCode/checkout',
  '/:regionCode/checkout-restore-cart',
  '/:regionCode/checkout-gateway',
]

const MODAL_OPENED_TIMEOUT_IN_SECONDS = 10
const WRAPPED_MODAL_COOKIE_NAME = 'wrappedDiscoverModalViewed'
const WRAPPED_MODAL_COOKIE_TTL_SECONDS = 3 * 24 * 60 * 60 // 3 days in seconds

function hasWrappedModalBeenViewed(): boolean {
  if (typeof document === 'undefined') return true
  return !!extractCookie(document.cookie, WRAPPED_MODAL_COOKIE_NAME)
}

function setWrappedModalViewed(): void {
  if (typeof document === 'undefined') return
  setCookie(WRAPPED_MODAL_COOKIE_NAME, 'true', WRAPPED_MODAL_COOKIE_TTL_SECONDS, '/', 'Lax')
}

function useShowWrappedDiscoverModal() {
  const [modalRecentlyOpened, setModalRecentlyOpened] = useState<boolean>(false)
  const showModal = useContext(ModalContext)
  const modalState = useContext(MasterModalContext)
  const dispatch = useAppDispatch()

  const userId = useAppSelector(state => state.auth.account.memberId)
  const firstName = useAppSelector(state => state.auth.account.givenName)
  const wrappedState = useAppSelector(state => state.wrapped.state)
  const wrappedData = useAppSelector(state => state.wrapped.data)
  const isInvalidRoute = !!useRouteMatch({ path: ResubModalExcludedRoutes, exact: false })

  // Fetch wrapped data when user is logged in
  useEffect(() => {
    if (userId) {
      dispatch(fetchWrapped())
    }
  }, [dispatch, userId])

  // Check cookie on mount
  const [alreadyViewed, setAlreadyViewed] = useState<boolean>(() => hasWrappedModalBeenViewed())

  // Track if we've already attempted to show the modal (guards against StrictMode double-runs)
  const hasAttemptedShow = useRef(false)

  // Prevent opening over other modals
  useEffect(() => {
    let openOtherModalTimeout: number

    if (modalState.open) {
      setModalRecentlyOpened(true)
    }

    if (!modalState.open && modalRecentlyOpened) {
      openOtherModalTimeout = window.setTimeout(() => {
        setModalRecentlyOpened(false)
      }, MODAL_OPENED_TIMEOUT_IN_SECONDS * 1000)
    }

    return () => {
      clearTimeout(openOtherModalTimeout)
    }
  }, [modalState.open, modalRecentlyOpened])

  const wrappedModalFlagActive = useOptimizelyExperiment(OptimizelyFeatureFlags.croWrappedModal)

  // Only show modal if wrapped data is loaded and available
  const hasWrappedData = wrappedState === 'done' && wrappedData !== undefined

  const canShowModal = !isInvalidRoute &&
    !modalRecentlyOpened &&
    !modalState.open &&
    !alreadyViewed &&
    hasWrappedData &&
    // Conditionals
    wrappedModalFlagActive &&
    wrappedData.stats.citiesVisited.length

  // Memoize callback that shows modal and sets cookie after modal opens
  const handleShowModal = useCallback((name: string) => {
    // Set cookie and state only after showModal promise starts
    // This ensures the modal is actually being shown before we mark it as viewed
    setWrappedModalViewed()
    setAlreadyViewed(true)
    showModal(<WrappedDiscoverModal firstName={name} />)
  }, [showModal])

  useEffect(() => {
    if (userId && firstName && canShowModal && !hasAttemptedShow.current) {
      hasAttemptedShow.current = true
      handleShowModal(firstName)
    }
  }, [handleShowModal, userId, firstName, canShowModal])
}

export default useShowWrappedDiscoverModal
